import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Check, X } from "lucide-react";
import { toast } from "sonner";
import { migrationApi } from "../../services/migrationApi";
import { formatApiErrorDetail } from "../../lib/api";
import { formatJst } from "../../lib/date";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { ReasonDialog } from "../../components/ReasonDialog";
import { NotFoundState } from "../../components/NotFoundState";
import { usePermissions } from "../../hooks/usePermissions";
import { useLocalization } from "../../hooks/useLocalization";

const Section = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5">
    <h3 className="text-sm font-semibold text-gray-800 mb-3">{title}</h3>
    {children}
  </div>
);

export default function MigrationProfileDetailPage() {
  const { employeeId: profileId } = useParams();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canManageMigration, canViewMigration } = usePermissions();
  const [profile, setProfile] = useState(null);
  const [aliases, setAliases] = useState([]);
  const [legalNameInput, setLegalNameInput] = useState("");
  const [preferredNameInput, setPreferredNameInput] = useState("");
  const [scheduleNameInput, setScheduleNameInput] = useState("");
  const [duplicateInput, setDuplicateInput] = useState("");
  const [noteInput, setNoteInput] = useState("");
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  const blockingIssues = (profile) => {
    const issues = [];
    if (!profile.confirmed_legal_name) issues.push(t("mgmtMigration.issueLegalNameUnconfirmed"));
    if (profile.employment_status === "REVIEW_REQUIRED") issues.push(t("mgmtMigration.issueEmploymentUnconfirmed"));
    if (profile.scheduling_eligibility === "REVIEW_REQUIRED") issues.push(t("mgmtMigration.issueSchedulingUnconfirmed"));
    if (profile.duplicate_status === "POSSIBLE_DUPLICATE") issues.push(t("mgmtMigration.issueDuplicateUnresolved"));
    return issues;
  };

  const load = async () => {
    const { data } = await migrationApi.getProfile(profileId);
    setProfile(data);
    setLegalNameInput(data.confirmed_legal_name || data.legal_name_source || "");
    setPreferredNameInput(data.confirmed_preferred_name || data.preferred_name || "");
    setScheduleNameInput(data.confirmed_schedule_name || data.schedule_name || "");
    const aliasResp = await migrationApi.getAliases(profileId);
    setAliases(aliasResp.data);
  };

  useEffect(() => {
    if (!canViewMigration) return;
    setNotFound(false);
    load().catch((err) => {
      if (err.response?.status === 404) setNotFound(true);
      else setError(t("mgmtMigration.failedToLoad"));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, canViewMigration]);

  const act = async (fn, successMsg) => {
    setError("");
    try {
      await fn();
      toast.success(successMsg);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!canViewMigration) return <p className="text-sm text-gray-500">{t("common.accessDenied")}</p>;
  if (notFound) return <NotFoundState testId="migration-profile-not-found" backTo="/management/workforce/migration-review" />;
  if (!profile) return <p className="text-sm text-gray-500" data-testid="migration-detail-loading">{t("common.loading")}</p>;

  const issues = blockingIssues(profile);
  const canApprove = canManageMigration && ["MIGRATION_REVIEW_REQUIRED", "UNDER_REVIEW"].includes(profile.profile_status) && issues.length === 0;
  const canActivate = canManageMigration && profile.profile_status === "APPROVED" && profile.account_status !== "CREATED";

  return (
    <div className="max-w-4xl space-y-5" data-testid="migration-detail-page">
      <PageHelpBanner pageKey="migration-detail" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtMigration.detailHelp")}</p>
      </PageHelpBanner>
      <button onClick={() => navigate("/management/workforce/migration-review")} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft size={15} /> {t("mgmtMigration.backToReview")}
      </button>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">{profile.proposed_employee_id}</h2>
          <p className="text-sm text-gray-500">{profile.legal_name_source} - {t("mgmtMigration.batch")} {profile.batch_id}</p>
        </div>
        <StatusBadge status={profile.profile_status} />
      </div>

      {issues.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="migration-blocking-issues">
          <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5 mb-1"><AlertTriangle size={15} /> {t("mgmtMigration.blockingIssuesBeforeApproval")}</p>
          <ul className="text-xs text-amber-700 list-disc list-inside">
            {issues.map((iss) => <li key={iss}>{iss}</li>)}
          </ul>
        </div>
      )}
      {error && <p className="text-sm text-red-500" data-testid="migration-detail-error">{error}</p>}

      <Section title={t("mgmtMigration.identityConfirmation")}>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input data-testid="legal-name-input" disabled={!canManageMigration} value={legalNameInput} onChange={(e) => setLegalNameInput(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm disabled:bg-gray-50" placeholder={t("mgmtMigration.legalName")} />
            {canManageMigration && (
              <button data-testid="confirm-legal-name-button" onClick={() => act(() => migrationApi.confirmLegalName(profileId, legalNameInput), t("mgmtMigration.legalNameConfirmed"))} className="px-3 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700">
                {t("mgmtMigration.confirm")}
              </button>
            )}
            {profile.confirmed_legal_name && <Check size={16} className="text-emerald-600" />}
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="preferred-name-input" disabled={!canManageMigration} value={preferredNameInput} onChange={(e) => setPreferredNameInput(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm disabled:bg-gray-50" placeholder={t("mgmtMigration.preferredName")} />
            {canManageMigration && (
              <button data-testid="confirm-preferred-name-button" onClick={() => act(() => migrationApi.confirmPreferredName(profileId, preferredNameInput), t("mgmtMigration.preferredNameConfirmed"))} className="px-3 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700">
                {t("mgmtMigration.confirm")}
              </button>
            )}
            {profile.confirmed_preferred_name && <Check size={16} className="text-emerald-600" />}
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="schedule-name-input" disabled={!canManageMigration} value={scheduleNameInput} onChange={(e) => setScheduleNameInput(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm disabled:bg-gray-50" placeholder={t("mgmtMigration.scheduleName")} />
            {canManageMigration && (
              <button data-testid="confirm-schedule-name-button" onClick={() => act(() => migrationApi.confirmScheduleName(profileId, scheduleNameInput), t("mgmtMigration.scheduleNameConfirmed"))} className="px-3 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700">
                {t("mgmtMigration.confirm")}
              </button>
            )}
            {profile.confirmed_schedule_name && <Check size={16} className="text-emerald-600" />}
          </div>
        </div>
      </Section>

      <Section title={t("mgmtMigration.aliases")}>
        <div className="space-y-2">
          {aliases.length === 0 && <p className="text-xs text-gray-500">{t("mgmtMigration.noAliasesRecorded")}</p>}
          {aliases.map((a) => (
            <div key={a.id} className="flex items-center justify-between border border-gray-100 rounded-lg p-2.5" data-testid={`alias-row-${a.id}`}>
              <div>
                <p className="text-sm font-medium text-gray-800">{a.alias_value} <span className="text-xs text-gray-500">({a.alias_type})</span></p>
                <p className="text-xs text-gray-500">{a.source_file} - {t("mgmtMigration.confidence")} {(a.confidence * 100).toFixed(0)}%</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={a.human_confirmation_status} />
                {canManageMigration && a.human_confirmation_status === "PENDING" && (
                  <>
                    <button data-testid={`confirm-alias-${a.id}`} onClick={() => act(() => migrationApi.reviewAlias(a.id, "CONFIRMED"), t("mgmtMigration.aliasConfirmed"))} className="p-1.5 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200"><Check size={13} /></button>
                    <button data-testid={`reject-alias-${a.id}`} onClick={() => act(() => migrationApi.reviewAlias(a.id, "REJECTED"), t("mgmtMigration.aliasRejected"))} className="p-1.5 rounded-full bg-red-100 text-red-700 hover:bg-red-200"><X size={13} /></button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title={t("mgmtMigration.employmentStatus")}>
        <div className="flex items-center gap-2 flex-wrap">
          {["CONFIRMED_ACTIVE", "CONFIRMED_INACTIVE", "CONFIRMED_MANAGEMENT_ONLY"].map((s) => (
            <button
              key={s}
              data-testid={`employment-status-${s}`}
              disabled={!canManageMigration}
              onClick={() => act(() => migrationApi.setEmploymentStatus(profileId, s), t("mgmtMigration.employmentStatusUpdated"))}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-50 ${profile.employment_status === s ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
            >
              {s}
            </button>
          ))}
        </div>
      </Section>

      <Section title={t("mgmtMigration.schedulingEligibility")}>
        <div className="flex items-center gap-2">
          {["ELIGIBLE", "NOT_ELIGIBLE"].map((s) => (
            <button
              key={s}
              data-testid={`scheduling-eligibility-${s}`}
              disabled={!canManageMigration}
              onClick={() => act(() => migrationApi.setSchedulingEligibility(profileId, s), t("mgmtMigration.schedulingEligibilityUpdated"))}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-50 ${profile.scheduling_eligibility === s ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
            >
              {s}
            </button>
          ))}
        </div>
      </Section>

      <Section title={t("mgmtMigration.wageConfiguration")}>
        <div className="flex items-center gap-2">
          {["REVIEW_REQUIRED", "CONFIGURED"].map((s) => (
            <button
              key={s}
              data-testid={`wage-status-${s}`}
              disabled={!canManageMigration}
              onClick={() => act(() => migrationApi.setWageStatus(profileId, s), t("mgmtMigration.wageStatusUpdated"))}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-50 ${profile.wage_configuration_status === s ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
            >
              {s}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">{t("mgmtMigration.wageConfigNote")}</p>
      </Section>

      <Section title={t("mgmtMigration.duplicateDetection")}>
        {profile.duplicate_status === "POSSIBLE_DUPLICATE" ? (
          <div className="space-y-2">
            <p className="text-sm text-amber-700 flex items-center gap-1.5"><AlertTriangle size={14} /> {t("mgmtMigration.flaggedAsDuplicateOf")} <code className="text-xs">{profile.duplicate_of}</code></p>
            {canManageMigration && (
              <button data-testid="merge-duplicate-button" onClick={() => act(() => migrationApi.mergeProfiles(profileId, profile.duplicate_of, true), t("mgmtMigration.profilesMerged"))} className="px-3 py-1.5 rounded-full bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700">
                {t("mgmtMigration.mergeKeepThisProfile")}
              </button>
            )}
          </div>
        ) : (
          canManageMigration && (
            <div className="flex items-center gap-2">
              <input data-testid="duplicate-candidate-input" value={duplicateInput} onChange={(e) => setDuplicateInput(e.target.value)} placeholder={t("mgmtMigration.otherProfileId")} className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm" />
              <button data-testid="mark-duplicate-button" onClick={() => act(() => migrationApi.markDuplicate(profileId, duplicateInput), t("mgmtMigration.markedAsPossibleDuplicate"))} className="px-3 py-2 rounded-lg bg-gray-200 text-gray-700 text-xs font-semibold hover:bg-gray-300">
                {t("mgmtMigration.markDuplicate")}
              </button>
            </div>
          )
        )}
      </Section>

      <Section title={t("mgmtMigration.reviewNotes")}>
        <div className="space-y-2 mb-3">
          {(profile.review_notes || []).map((n, i) => (
            <div key={i} className="text-xs text-gray-600 border-l-2 border-gray-200 pl-2">
              <span className="font-semibold">{n.by_name}</span> - {formatJst(n.at)}: {n.note}
            </div>
          ))}
        </div>
        {canManageMigration && (
          <div className="flex items-center gap-2">
            <input data-testid="review-note-input" value={noteInput} onChange={(e) => setNoteInput(e.target.value)} placeholder={t("mgmtMigration.addReviewNote")} className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            <button data-testid="add-review-note-button" onClick={() => act(() => migrationApi.addNote(profileId, noteInput).then(() => setNoteInput("")), t("mgmtMigration.noteAdded"))} className="px-3 py-2 rounded-lg bg-gray-200 text-gray-700 text-xs font-semibold hover:bg-gray-300">
              {t("mgmtMigration.addNote")}
            </button>
          </div>
        )}
      </Section>

      {canManageMigration && (
        <div className="flex items-center gap-3 bg-white rounded-xl border border-gray-200 p-5">
          <button
            data-testid="approve-profile-button"
            disabled={!canApprove}
            onClick={() => act(() => migrationApi.approveProfile(profileId), t("mgmtMigration.profileApproved"))}
            className="px-5 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t("common.approve")}
          </button>
          <button
            data-testid="reject-profile-button"
            disabled={["REJECTED", "DUPLICATE_MERGED"].includes(profile.profile_status)}
            onClick={() => setShowRejectDialog(true)}
            className="px-5 py-2 rounded-full bg-red-100 text-red-700 text-sm font-semibold hover:bg-red-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t("common.reject")}
          </button>
          <button
            data-testid="activate-profile-button"
            disabled={!canActivate}
            onClick={() => act(() => migrationApi.activateProfile(profileId), t("mgmtMigration.employeeProfileActivated"))}
            className="px-5 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t("mgmtMigration.activateEmployeeProfile")}
          </button>
          {profile.converted_employee_id && (
            <Link to={`/management/employees/${profile.converted_employee_id}`} className="text-sm text-blue-600 hover:underline ml-auto" data-testid="view-converted-employee-link">
              {t("mgmtMigration.viewEmployeeProfileArrow")}
            </Link>
          )}
        </div>
      )}

      {showRejectDialog && (
        <ReasonDialog
          open onOpenChange={setShowRejectDialog} title={t("mgmtMigration.rejectMigrationProfile")} danger
          contextItems={[
            { label: t("mgmtMigration.profile"), value: profile.proposed_employee_id },
            { label: t("mgmtMigration.legalName"), value: profile.legal_name_source },
          ]}
          reasonLabel={t("mgmtMigration.reasonForRejection")} confirmLabel={t("mgmtMigration.rejectProfile")}
          onConfirm={(reason) => act(() => migrationApi.rejectProfile(profileId, reason), t("mgmtMigration.profileRejected"))}
          testId="reject-migration-profile-dialog"
        />
      )}
    </div>
  );
}
